'use client';
import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { motion, Transition } from 'framer-motion';
import { CircleBlur, ICircleBlur } from '../backgrounds/CircleBlur';
import { useWindowDimensions } from '../../hooks/useWindowDimensions';
import useOnScreen from '../../hooks/useIntersectionObserver';
import { ITier } from '../../data/tiers';
import { PriceTag } from './elements/PriceTag';

export interface IPricingTierItem extends PropsWithChildren {
    tier: ITier;
    color: ICircleBlur['color'];
    popular?: boolean;
    animationDirection?: 'left' | 'right' | 'top';
}

export const PricingTierItem: React.FC<IPricingTierItem> = (props) => {
    const { tier, color, children, popular, animationDirection } = props;

    const ref = useRef<HTMLDivElement | null>(null);

    const { isMobile } = useWindowDimensions();

    const isOnScreen = useOnScreen(ref, isMobile ? '0px' : '-150px', 0.5);

    const [show, setShow] = useState(false);

    useEffect(() => {
        if (isOnScreen && !show) {
            setShow(true);
        }
    }, [isOnScreen, show]);

    const borderStyles = useMemo(() => {
        if (popular) {
            return 'buttonGradient';
        }

        return 'border-neutral-500 border-[3px]';
    }, [popular]);

    const verticalStyles = useMemo(() => {
        if (popular) {
            return 'shadow-sm md:shadow-xl shadow-cloudbarPrimary-light';
        }

        return 'md:pt-10 ';
    }, [popular]);

    // motion.div based on animationDirection
    const animate = useMemo(() => {
        if (isMobile) {
            return { opacity: 1 };
        }

        switch (animationDirection) {
            case 'left':
                return { x: '-20px', opacity: 1 };
            case 'right':
                return { x: '20px', opacity: 1 };
            case 'top':
                return { y: '0px', opacity: 1 };
            default:
                return {};
        }
    }, [animationDirection, isMobile]);

    const transition = useMemo((): Transition | undefined => {
        if (isMobile) {
            return { duration: 0.5, ease: 'easeOut' };
        }

        switch (animationDirection) {
            case 'left':
            case 'right':
                return {
                    duration: 0.25,
                    ease: 'easeIn',
                    delay: 0.5,
                };
        }
        return { duration: 0.5, ease: 'easeOut' };
    }, [animationDirection, isMobile]);

    const inital = useMemo(() => {
        if (isMobile) {
            return { opacity: 0.2 };
        }

        switch (animationDirection) {
            case 'left':
                return { x: '10px', opacity: 0.2 };
            case 'right':
                return { x: '-10px', opacity: 0.2 };
            case 'top':
                return { y: '-20px', opacity: 0.2 };
            default:
                return { opacity: 0.2, y: '30px' };
        }
    }, [animationDirection, isMobile]);

    return (
        <motion.div
            initial={inital}
            animate={show ? animate : {}}
            transition={transition}
            className={`relative flex h-full w-full rounded-xl ${verticalStyles}`}
        >
            <div
                ref={ref}
                className={`relative flex w-full flex-col gap-4 overflow-hidden rounded-xl ${borderStyles}`}
            >
                <div
                    className={`flex h-full w-full flex-col gap-4 overflow-hidden rounded-xl bg-neutral-900 px-8 py-6`}
                >
                    <h2 className="w-full font-bold text-3xl text-neutral-50">{tier.title}</h2>
                    <span className="text-md font-medium text-neutral-50">{tier.subtitle}</span>

                    <div className="relative mt-6 flex w-full flex-col gap-4">{children}</div>

                    <PriceTag tier={tier} />

                    <CircleBlur color={color} className="opacity-50" />
                </div>
            </div>
        </motion.div>
    );
};
